/* eslint-disable max-lines */
import { isNil, orderBy } from 'lodash';

import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa } from 'lib/helpers/utility';

import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import { calcLatestBenefits } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/calcLatestBenefits';
import {
  modifyPackages,
  modifyProducts
} from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/internal/configs';
import {
  denied_codes,
  patient_responsibility
} from 'components/practice/BeyondBilling/lib/insuranceAdjustmentGroups';
import { MAX_PERCENT_VALUE } from 'components/shared/CurrencyInput/lib/constants';

const INS_TYPES = ['primary', 'secondary', 'tertiary'];

const calculateAdjustmentsFromLast = (adjustments) => {
  if (!ia(adjustments)) return { ins_payment: 0, pt_adjustment: 0 };

  const lastAdjustment = adjustments[adjustments.length - 1] || {};
  const ins_payment = Math.round(lastAdjustment?.paid ?? 0) || 0;
  let pt_adjustment = 0;

  if (ia(lastAdjustment?.pt_adj)) {
    for (const { group, code, amount } of lastAdjustment.pt_adj) {
      if (lastAdjustment?.patient_responsibility === false) {
        if (!(group === 'PR' && +code === 45)) {
          pt_adjustment += amountToCents(amount);
        }
      } else {
        pt_adjustment += amountToCents(amount);
      }
    }
  }

  if (ia(lastAdjustment?.ins_adj)) {
    for (const { group, code, amount } of lastAdjustment.ins_adj) {
      const hasPRCode = patient_responsibility[group]?.includes(+code);
      const hasDeniedCode = denied_codes[group]?.includes(code);

      if (lastAdjustment?.patient_responsibility === false) {
        if (!(group === 'PR' && +code === 45)) {
          if (group === 'PR' || hasPRCode || hasDeniedCode) {
            pt_adjustment += amountToCents(amount);
          }
        }
      } else {
        if (group === 'PR' || hasPRCode || hasDeniedCode) {
          pt_adjustment += amountToCents(amount);
        }
      }
    }
  }

  return { ins_payment, pt_adjustment };
};

const primaryCalculation = (procedure) => {
  let charge = 0;
  let ins_payment = 0;
  let ins_adjustment = 0;
  let pt_adjustment = 0;
  const other_adj = procedure?.other_adj ?? 0;

  charge += ['self_pay', 'cash'].includes(procedure.type)
    ? (procedure.pt_balance ?? +procedure?.total_cost_cents ?? 0)
    : (procedure.charge ?? 0);

  for (const insType of INS_TYPES) {
    const adjustments = procedure?.[insType];
    if (!adjustments) continue;

    const { ins_payment: insPay, pt_adjustment: ptAdj } = calculateAdjustmentsFromLast(adjustments);

    ins_payment += insPay;
    pt_adjustment += ptAdj;
  }

  let insAdj =
    numOrDefault(charge) -
    numOrDefault(ins_payment) -
    numOrDefault(calcLatestBenefits({ procedure })?.ptBalance);

  if (['product', 'package', 'self_pay'].includes(procedure.type) || procedure?.denied) {
    insAdj = 0;
  }

  ins_adjustment += insAdj;

  return {
    ...procedure,
    charge,
    ins_adjustment,
    pt_adjustment,
    ins_payment,
    other_adj
  };
};

const secondCalculation = (procedure) => {
  const { ins_payment = 0, pt_balance = 0 } = procedure;
  const final_ins_payment = ins_payment - pt_balance;
  return Math.max(final_ins_payment, 0);
};

const calculateProceduresTotal = (procedures = []) => {
  return procedures?.reduce(
    (acc, procedure) => {
      const qty = procedure?.quantity ?? procedure?.units ?? procedure?.sales_count ?? 1;
      const qtyToMultiply =
        !procedure?.type || procedure?.type?.toLowerCase() === 'product' ? 1 : qty;

      let result = {};

      if (Object.keys(procedure).includes('primary')) {
        result = primaryCalculation(procedure);

        acc.local_tax += numOrDefault(result.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(result.state_tax_rate * qtyToMultiply);
        acc.charge += numOrDefault(result.charge * qtyToMultiply);
        acc.ins_adjustment += numOrDefault(result.ins_adjustment * qtyToMultiply);
        acc.ins_payment += numOrDefault(result.ins_payment * qtyToMultiply);
        acc.pt_adjustment += numOrDefault(result.pt_adjustment);
        acc.other_adj += numOrDefault(result.other_adj);
      } else if (
        procedure?.ins_payment > 0 &&
        procedure?.ins_adjustment > 0 &&
        procedure?.charge === procedure?.ins_payment + procedure?.ins_adjustment
      ) {
        const ins_payment = secondCalculation(procedure);

        acc.local_tax += numOrDefault(procedure.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(procedure.state_tax_rate * qtyToMultiply);
        acc.charge += (procedure.charge ?? procedure.pt_balance ?? 0) * qtyToMultiply;
        acc.ins_adjustment += numOrDefault(procedure.ins_adjustment * qtyToMultiply);
        acc.ins_payment += numOrDefault(ins_payment * qtyToMultiply);
        acc.pt_adjustment += numOrDefault(procedure.pt_adjustment);
        acc.other_adj += numOrDefault(procedure.other_adj);
      } else if (procedure?.status !== 'cancelled') {
        let {
          charge = 0,
          total_amount_cents = 0,
          pt_balance,
          total_cost_cents = 0,
          charge_type,
          ins_payment,
          ins_adjustment,
          other_adj = 0,
          type,
          denied = false
        } = procedure;

        let insAdj = 0;

        if (type !== 'primary') {
          insAdj = Math.max(charge - pt_balance, 0);
        }

        if (['product', 'package', 'self_pay', 'cash', 'unapplied'].includes(type) || denied) {
          insAdj = 0;
          charge = 0;
        }

        if (
          isNil(pt_balance) &&
          charge_type === 'copay' &&
          isNil(ins_payment) &&
          isNil(ins_adjustment)
        ) {
          insAdj = charge;
        }

        acc.local_tax += numOrDefault(procedure.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(procedure.state_tax_rate * qtyToMultiply);
        acc.charge +=
          ((+charge || pt_balance) ?? (+total_cost_cents || +total_amount_cents)) * qtyToMultiply;
        acc.ins_adjustment += numOrDefault(insAdj * qtyToMultiply);
        acc.pt_adjustment += numOrDefault(pt_balance);
        acc.other_adj += numOrDefault(other_adj);
      }

      acc.qty += qty;
      return acc;
    },
    {
      charge: 0,
      ins_adjustment: 0,
      pt_adjustment: 0,
      ins_payment: 0,
      other_adj: 0,
      qty: 0,
      local_tax: 0,
      state_tax: 0
    }
  );
};

export const getProceduresTotal = (invoices = []) => {
  const filteredInvoices = invoices.filter((invoice) => invoice.show);
  const procedures = filteredInvoices
    .map((invoice) => iaRa(invoice.procedures).filter((p) => p.show))
    .flat();
  const products = filteredInvoices
    .map((invoice) =>
      modifyProducts({
        products: iaRa(invoice.products).filter((p) => p.show),
        isTotal: true
      })
    )
    .flat();

  const packages = filteredInvoices
    .map((invoice) =>
      modifyPackages({
        packages: iaRa(invoice.packages).filter((p) => p.show),
        isTotal: true
      })
    )
    .flat();

  const services = [...procedures, ...products, ...packages];

  const invoiceTotals = filteredInvoices.reduce(
    (acc, invoice) => ({
      amount_paid: acc.amount_paid + (invoice?.amount_paid ?? 0),
      discount: acc.discount + (invoice?.discount?.amount_cents ?? 0),
      discount_type: invoice?.discount?.type ?? 'amount',
      tax: acc.tax + (invoice?.tax ?? 0),
      write_off: acc.write_off + (invoice?.write_off ?? 0),
      ptBalance: acc.ptBalance + (invoice?.invoice_balance ?? invoice?.balance ?? 0),
      totalAmount: acc.totalAmount + (invoice?.total_amount ?? 0)
    }),
    { amount_paid: 0, discount: 0, tax: 0, write_off: 0, ptBalance: 0, totalAmount: 0 }
  );

  return {
    ...calculateProceduresTotal(services),
    ...invoiceTotals
  };
};

export const getInvoicesDetails = (invoices = []) =>
  orderBy(invoices, ['dateofservice'], 'desc').map((invoice) => ({
    id: invoice.id,
    dateOfService: invoice.dateofservice,
    discount: invoice?.discount?.amount_cents ?? 0,
    procedures: invoice.procedures,
    products: invoice.products,
    packages: invoice.packages,
    tax: invoice.tax ?? 0,
    amount_paid: invoice.amount_paid ?? 0,
    write_off: invoice.write_off ?? 0,
    created_at: invoice.created_at
  }));

export const calculateAmountFromPercentage = (percentage, baseTotal) =>
  baseTotal > 0 ? Math.round((percentage / MAX_PERCENT_VALUE) * baseTotal) : 0;

export const calculatePercentageFromAmount = (amountCents, baseTotal) =>
  baseTotal > 0 ? Math.round((amountCents / baseTotal) * MAX_PERCENT_VALUE) : 0;
